// =============================================
// GESTIÓN DE USUARIOS
// Con funcionalidad de gestión de contraseñas
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import userService from '../../services/userService';
import UserForm from './UserForm';
import PasswordDialog from './PasswordDialog';

const Users = () => {
  console.log('🔑 USANDO USERS.JSX CON PASSWORD DIALOG'); // ← AGREGAR ESTA LÍNEA
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

  // Estados para ordenamiento y paginación
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cargar usuarios
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userService.getAll();
      setUsers(response.users || []);
    } catch (err) {
      setError('Error al cargar usuarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Manejar ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Ayudantes para ordenamiento
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Manejar paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Abrir formulario para nuevo usuario
  const handleAdd = () => {
    setEditingUser(null);
    setFormOpen(true);
  };

  // Abrir formulario para editar
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormOpen(true);
  };

  // Eliminar usuario
  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) {
      return;
    }

    try {
      await userService.delete(id);
      loadUsers();
    } catch (err) {
      setError('Error al eliminar usuario');
      console.error(err);
    }
  };

  // Abrir dialog de contraseña
  const handleOpenPasswordDialog = (user) => {
    setSelectedUserForPassword(user);
    setPasswordDialog(true);
  };

  // Cerrar formulario
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingUser(null);
  };

  // Después de guardar
  const handleSaveSuccess = () => {
    loadUsers();
    handleCloseForm();
  };

  // Color del rol
  const getRoleColor = (role) => {
    return role === 'admin' ? 'primary' : 'success';
  };

  // Texto del rol
  const getRoleLabel = (role) => {
    return role === 'admin' ? 'Administrador' : 'Propietario';
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Nuevo Usuario
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  <strong>Nombre</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  <strong>Email</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>Teléfono</strong></TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'house_number'}
                  direction={orderBy === 'house_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('house_number')}
                >
                  <strong>Casa #</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  <strong>Rol</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No hay usuarios registrados
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              stableSort(users, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>{user.phone || '-'}</TableCell>
                    <TableCell align="center">
                      {user.house_number ? (
                        <Chip label={`#${user.house_number}`} size="small" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getRoleLabel(user.role)}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {/* Botón de contraseña (solo para owners) */}
                      {user.role === 'owner' && (
                        <IconButton
                          onClick={() => handleOpenPasswordDialog(user)}
                          color="primary"
                          size="small"
                          title="Gestionar Contraseña"
                        >
                          <KeyIcon />
                        </IconButton>
                      )}
                      {/* Botón editar */}
                      <IconButton
                        onClick={() => handleEdit(user)}
                        color="primary"
                        size="small"
                        title="Editar"
                      >
                        <EditIcon />
                      </IconButton>
                      {/* Botón eliminar */}
                      <IconButton
                        onClick={() => handleDelete(user.id)}
                        color="error"
                        size="small"
                        title="Eliminar"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>

      {/* Formulario de usuario */}
      <UserForm
        open={formOpen}
        onClose={handleCloseForm}
        user={editingUser}
        onSuccess={handleSaveSuccess}
      />

      {/* Dialog de gestión de contraseña */}
      <PasswordDialog
        open={passwordDialog}
        onClose={() => {
          setPasswordDialog(false);
          setSelectedUserForPassword(null);
        }}
        user={selectedUserForPassword}
        onSuccess={loadUsers}
      />
    </Container>
  );
};

export default Users;